import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  Vote,
  CheckCircle,
  Clock,
  Users,
  ThumbsUp,
  LogIn
} from 'lucide-react';
import { Link } from 'react-router-dom';

const PollsPage = () => {
  const { user, token } = useAuth();
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [votedPolls, setVotedPolls] = useState(new Set());

  useEffect(() => {
    fetchPolls();
  }, []);

  const fetchPolls = async () => {
    try {
      const response = await axios.get(`${API}/polls`);
      
      // Get results for each poll
      const pollsWithResults = await Promise.all(
        response.data.map(async (poll) => {
          try {
            const resultRes = await axios.get(`${API}/polls/${poll.id}`);
            return { ...poll, ...resultRes.data };
          } catch {
            return poll;
          }
        })
      );
      
      setPolls(pollsWithResults);
    } catch (error) {
      console.error('Failed to fetch polls:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (pollId, optionIndex) => {
    if (!user) {
      toast.error('Please sign in to vote');
      return;
    }

    try {
      await axios.post(`${API}/polls/${pollId}/vote`, 
        { option_index: optionIndex },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      toast.success('Vote recorded!');
      setVotedPolls(prev => new Set([...prev, pollId]));
      fetchPolls();
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('You have already voted on this poll');
        setVotedPolls(prev => new Set([...prev, pollId]));
      } else {
        toast.error('Failed to submit vote');
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-plp-blue border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-plp-blue to-plp-blue-light text-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-4 mb-4">
            <Vote className="w-12 h-12 text-plp-gold" />
            <h1 className="font-heading text-4xl font-bold">Community Polls</h1>
          </div>
          <p className="text-xl text-blue-100 max-w-2xl">
            Your voice matters! Vote on community issues and help shape the future of Killarney.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-12">
        {!user && (
          <Card className="mb-8 border-plp-gold bg-plp-gold/5">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <LogIn className="w-5 h-5 text-plp-gold" />
                <span className="text-slate-700">Sign in to vote on community polls</span>
              </div>
              <Link to="/auth">
                <Button className="bg-plp-gold hover:bg-plp-gold/90 text-plp-blue">
                  Sign In
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {polls.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Vote className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-600">No active polls</h3>
              <p className="text-slate-500 mt-1">Check back later for new community polls</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {polls.map((poll) => {
              const totalVotes = poll.results?.total_votes || 0;
              const hasVoted = votedPolls.has(poll.id);
              
              return (
                <Card key={poll.id} className="overflow-hidden">
                  <CardHeader className="bg-slate-50 border-b">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" /> Active
                      </span>
                      {poll.ends_at && (
                        <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full flex items-center gap-1">
                          <Clock className="w-3 h-3" /> Ends {new Date(poll.ends_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                    <CardTitle className="text-xl">{poll.question}</CardTitle>
                    {poll.description && (
                      <p className="text-slate-500 mt-1">{poll.description}</p>
                    )}
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="space-y-3">
                      {(poll.options || []).map((option, idx) => {
                        const voteCount = poll.results?.vote_counts?.[idx] || 0;
                        const percentage = totalVotes > 0 ? ((voteCount / totalVotes) * 100).toFixed(1) : 0;
                        
                        return (
                          <button
                            key={`${poll.id}-option-${idx}`}
                            onClick={() => handleVote(poll.id, idx)}
                            disabled={hasVoted || !user}
                            className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                              hasVoted 
                                ? 'border-slate-200 cursor-default' 
                                : user 
                                  ? 'border-slate-200 hover:border-plp-blue hover:bg-plp-blue/5 cursor-pointer' 
                                  : 'border-slate-200 cursor-not-allowed opacity-70'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-medium text-slate-800">{option}</span>
                              <span className="text-sm text-slate-500">{voteCount} votes ({percentage}%)</span>
                            </div>
                            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-plp-blue transition-all duration-500"
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    
                    <div className="flex items-center justify-between mt-6 pt-4 border-t">
                      <span className="text-sm text-slate-500 flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {totalVotes} total votes
                      </span>
                      {hasVoted && (
                        <span className="text-sm text-emerald-600 flex items-center gap-1">
                          <ThumbsUp className="w-4 h-4" />
                          You voted!
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default PollsPage;
